/*
 * Safegear - variant-level specification row updater (Horizon 4.1.3)
 *
 * WHY THIS EXISTS
 * Variant-level rows (door frame, wall opening, clear opening) must follow the
 * selected variant. On vault-door-at1-22 Door Frame Height is 2190 mm for Block
 * Frame and 2220 mm for Corner Frame; showing the wrong one misstates the wall
 * opening the customer has to build.
 *
 * HOW HORIZON ACTUALLY WORKS  (read assets/variant-picker.js before editing)
 *   - <variant-picker> dispatches ProductSelectEvent from '@shopify/events' on
 *     itself. It bubbles, which is why vendor components can listen on
 *     closest('.shopify-section'). A document-level listener therefore works.
 *   - The event carries `event.promise`. The payload is NOT on the event at
 *     dispatch time.
 *   - On a normal product page buildRequestUrl() returns the product URL with
 *     `option_values`, with NO section_id, so the resolved `detail.html` is the
 *     FULL new page document. Horizon morphs only the variant picker itself;
 *     every other component reads what it needs out of detail.html. That is the
 *     cheap path and the one used here.
 *   - `detail.resource` is the parsed variant JSON; `resource.id` is the variant
 *     id (product-price.js assigns it to an input named "id").
 *
 * EARLIER MISTAKES - do not repeat
 *   1. `variant:update` / `variant:selected`: 2.1.6 events. Horizon 4.1.3's
 *      ThemeEvents (assets/events.js) has NO variant event at all.
 *   2. Reading `event.detail` synchronously: empty. Use event.promise.
 *   3. Loading this file as a classic script: the imports below are a
 *      SyntaxError unless the section tag carries type="module".
 */

import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const ROOT = '[data-sg-metafields]';
const CONTENT = '.sg-metafields__content';

/**
 * Preferred path: lift the already-fetched markup out of detail.html.
 * @returns {boolean} true when at least one section was updated.
 */
function updateFromHtml(html) {
  if (!html || typeof html.querySelectorAll !== 'function') return false;

  const targets = document.querySelectorAll(ROOT);
  const sources = html.querySelectorAll(ROOT);
  if (!targets.length || !sources.length) return false;

  let updated = 0;

  targets.forEach((target, index) => {
    const source = sources[index] || sources[0];
    const nextContent = source.querySelector(CONTENT);
    const currentContent = target.querySelector(CONTENT);
    if (!nextContent || !currentContent) return;
    currentContent.replaceWith(nextContent.cloneNode(true));
    updated += 1;
  });

  return updated > 0;
}

/**
 * Fallback: ask Horizon to re-render this section on its own. Needed where the
 * picker requested a single section (quick add, product cards, featured
 * product), so detail.html does not contain us.
 */
function updateViaRenderer(variantId) {
  const roots = document.querySelectorAll(ROOT);
  if (!roots.length) return;

  const url = new URL(window.location.href);
  if (variantId) url.searchParams.set('variant', String(variantId));

  roots.forEach((root) => {
    const section = root.closest('.shopify-section');
    if (!section || !section.id) return;

    sectionRenderer.renderSection(section.id, { url, cache: false }).catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[sg-metafields] section re-render failed:', error);
      }
    });
  });
}

/**
 * 2026.10.13: other variant-dependent bits outside the spec table, e.g. the
 * "Depth incl. lock/handle" line in sections/sg-size-comparison.liquid. Every
 * element with data-sg-variant-sync="<key>" is replaced by the element with
 * the same key in the re-rendered page. Elements missing from either side are
 * left alone.
 */
function syncVariantBits(html) {
  if (!html || typeof html.querySelectorAll !== 'function') return;
  document.querySelectorAll('[data-sg-variant-sync]').forEach((target) => {
    const key = target.getAttribute('data-sg-variant-sync');
    const source = html.querySelector('[data-sg-variant-sync="' + CSS.escape(key) + '"]');
    if (source) target.replaceWith(source.cloneNode(true));
  });
}

function onProductSelect(event) {
  // Variant swatches on product cards dispatch this too; ignore them.
  if (event.target instanceof Element && event.target.closest('product-card')) return;

  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') {
    console.warn('[sg-metafields] productSelect fired without a promise; nothing to do.');
    return;
  }

  promise
    .then(({ detail }) => {
      syncVariantBits(detail?.html);
      if (updateFromHtml(detail?.html)) return;
      updateViaRenderer(detail?.resource?.id);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[sg-metafields] product select promise rejected:', error);
      }
    });
}

document.addEventListener(StandardEvents.productSelect, onProductSelect);
